import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Copy, Play, Trash2, TriangleAlert } from 'lucide-react';
import { useSnackbar } from 'notistack';

import { AUTO_DIRECTOR_CAMERA_LABEL } from '@/features/clips/types';
import useConfirmationDialogContext from '@/shared/hooks/useConfirmationDialogContext';
import { cx } from '@/styles/muiTheme';

import { useDeleteWscStream, useLaunchWscStream } from '../hooks/wscStreamQueries';
import {
  IN_FLIGHT_WSC_STATUSES,
  LAUNCHABLE_WSC_STATUSES,
  TERMINAL_WSC_STATUSES,
  type WscStream,
} from '../types';
import { WscBadge } from './WscBadge';
import { WscStreamStatusPill } from './WscStreamStatusPill';

/**
 * Status · Camera · Scheduled · Instance · Endpoint · actions.
 *
 * Exported so WscGameCard's header labels sit on the identical track list —
 * two grids that have to line up should not be able to drift apart.
 *
 * Two tracks are deliberately off the design's numbers. Status is wider than
 * its 96px because a real status is up to nine tracked-out monospace
 * characters (`LAUNCHING`) inside a pill, not the bare word. Endpoint gets a
 * 360px floor because it shows the whole FQDN: the longest shape we generate
 * is `srt://{abbr}-{id}.{league}.egress.{suffix}:9000`, ~52 characters, which
 * is ~345px of 11px mono plus the copy glyph. Camera and Instance give up the
 * width for it — `i-0390cc9273c7a0e9d` is a fixed 19 characters and never
 * needed 180px.
 */
export const WSC_STREAM_GRID =
  '120px minmax(160px,1fr) 104px minmax(150px,0.8fr) minmax(360px,2fr) 76px';

export const WSC_STREAM_GRID_GAP = '16px';

interface Props {
  stream: WscStream;
  gameUuid: string;
  /**
   * When true, the delete control is not rendered. Used for games whose
   * status is no longer 'scheduled' — i.e. nothing the operator can change
   * about already-running or finished games on this page.
   */
  readOnly?: boolean;
  /**
   * True once the game's status is 'recorded'. By then the instance has been
   * torn down and its Route53 record deleted with it, so the endpoint is a
   * historical value that no longer resolves — it stays visible as a record
   * of what ran, but it is not offered for copying.
   */
  gameIsFinal?: boolean;
}

/** Shared by the action buttons: ghost until hovered, per the design. */
const actionButtonSx = {
  width: 28,
  height: 28,
  borderRadius: '4px',
  color: cx.text.tertiary,
  '&:hover': { color: cx.text.primary, bgcolor: cx.bg.elevated },
  '&.Mui-disabled': { color: cx.border.default },
};

interface EndpointValueProps {
  url: string;
  /** False on a recorded game or a dead stream: nothing left to connect to. */
  copyable: boolean;
  /** True once RUNNING; before then the name exists but does not resolve. */
  resolves: boolean;
}

/**
 * One SRT URL in the Endpoint cell: the stream's own hostname, or the WSC
 * alias under it. Both carry the same copy affordance, so it lives here once.
 */
function EndpointValue({ url, copyable, resolves }: EndpointValueProps) {
  const { enqueueSnackbar } = useSnackbar();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar('Copied to clipboard', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to copy', { variant: 'error' });
    }
  };

  // Rendered by both branches — inside the copy target for a live
  // game, bare for a recorded one — so the two cannot drift apart visually.
  const label = (
    <Box
      component="code"
      title={url}
      sx={{
        fontFamily: cx.fontMono,
        fontSize: 11,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        // Only reachable if the viewport squeezes the grid below its
        // 360px floor; at any normal width the whole FQDN is shown.
        textOverflow: 'ellipsis',
        color: cx.text.secondary,
        // Dimmed until RUNNING, and dashed-bordered before then, to
        // keep the old row's warning that the name exists but does
        // not resolve yet.
        opacity: resolves ? 1 : 0.7,
      }}
    >
      {url}
    </Box>
  );

  if (!copyable) {
    // Recorded game or dead stream: the instance is torn down and its
    // DNS record with it, so there is nothing left to connect to. The
    // whole cell is the click target, so dropping only the glyph would
    // leave an invisible copy behind — the entire affordance goes.
    return <Box sx={{ display: 'flex', minWidth: 0 }}>{label}</Box>;
  }

  return (
    <Tooltip
      title={
        resolves
          ? 'Click to copy'
          : "Click to copy — DNS won't resolve until the stream is UP"
      }
      placement="top"
    >
      {/* One click target, not a label plus a button: the whole cell
          already copied in the old row, and two handlers on the same
          value would fire two snackbars when the glyph is clicked. */}
      <Box
        onClick={copy}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: 0,
          cursor: 'pointer',
          '&:hover .wsc-copy': { color: cx.text.primary, borderColor: cx.border.default },
        }}
      >
        {label}
        <Box
          className="wsc-copy"
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
            width: 22,
            height: 22,
            border: '1px solid',
            borderColor: cx.border.strong,
            borderStyle: 'solid',
            borderRadius: '4px',
            color: cx.text.tertiary,
          }}
        >
          <Copy size={12} />
        </Box>
      </Box>
    </Tooltip>
  );
}

export function WscStreamRow({ stream, gameUuid, readOnly = false, gameIsFinal = false }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const setConfirmationDialog = useConfirmationDialogContext();
  const deleteStream = useDeleteWscStream(gameUuid);
  const launchStream = useLaunchWscStream(gameUuid);

  const isTerminal = TERMINAL_WSC_STATUSES.includes(stream.status);
  const isInFlight = IN_FLIGHT_WSC_STATUSES.includes(stream.status);
  const isLaunchable = LAUNCHABLE_WSC_STATUSES.includes(stream.status);
  const isRunning = stream.status === 'RUNNING';

  

  const isCopyable = !gameIsFinal && !isTerminal;
  
  // Auto-director rows carry no gameCameraId, so the numeric fallback would
  // render "camera #null" — show the same label the clips flow uses instead.
  const cameraName = stream.autoDirector
    ? AUTO_DIRECTOR_CAMERA_LABEL
    : (stream.gameCamera?.displayName ?? `camera #${stream.gameCameraId}`);

  const endpoint = stream.hostname ? `srt://${stream.hostname}:9000` : null;
  const cnameEndpoint = stream.cname ? `srt://${stream.cname}:9000` : null;

  const handleLaunch = () => {
    setConfirmationDialog({
      title: 'Start stream now',
      message: (
        <Typography>
          This will launch the instance for{' '}
          <Typography component="span" sx={{ fontWeight: 700 }}>
            {cameraName}
          </Typography>{' '}
          immediately instead of waiting for the scheduled warmup.
        </Typography>
      ),
      confirmText: 'Start now',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await launchStream.mutateAsync(stream.id);
          enqueueSnackbar(`Launch enqueued for stream #${stream.id}`, { variant: 'success' });
        } catch (err: any) {
          const msg = err?.response?.data?.message ?? err?.message ?? 'Launch failed';
          enqueueSnackbar(msg, { variant: 'error' });
          throw err;
        }
      },
    });
  };

  const handleDelete = () => {
    setConfirmationDialog({
      confirmButtonStyle: 'error',
      title: isInFlight ? 'Tear down stream' : 'Delete stream',
      message: (
        <Typography>
          {isInFlight ? (
            <>
              This will cut the stream and tear down the instance for{' '}
              <Typography component="span" sx={{ fontWeight: 700 }}>
                {cameraName}
              </Typography>
              . This action cannot be undone.
            </>
          ) : (
            <>
              Stream{' '}
              <Typography component="span" sx={{ fontWeight: 700 }}>
                #{stream.id}
              </Typography>{' '}
              for{' '}
              <Typography component="span" sx={{ fontWeight: 700 }}>
                {cameraName}
              </Typography>{' '}
              will be removed from the schedule.
            </>
          )}
        </Typography>
      ),
      confirmText: isInFlight ? 'Tear down' : 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await deleteStream.mutateAsync(stream.id);
          const message = isInFlight
            ? `Teardown enqueued for stream #${stream.id}`
            : `Stream #${stream.id} deleted`;
          enqueueSnackbar(message, { variant: 'success' });
        } catch (err: any) {
          const msg = err?.response?.data?.message ?? err?.message ?? 'Delete failed';
          enqueueSnackbar(msg, { variant: 'error' });
          throw err;
        }
      },
    });
  };

  const scheduledAt = stream.scheduledFor ? new Date(stream.scheduledFor) : null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: WSC_STREAM_GRID,
        gap: WSC_STREAM_GRID_GAP,
        alignItems: 'center',
        px: '20px',
        py: '14px',
        borderBottom: '1px solid',
        borderColor: cx.border.subtle,
        color: cx.text.secondary,
        fontSize: 12,
        lineHeight: '16px',
        letterSpacing: '0.15px',
        '&:last-of-type': { borderBottom: 0 },
        '&:hover': { bgcolor: cx.bg.surface2 },
      }}
    >
      {/* Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
        <WscStreamStatusPill status={stream.status} />
        {stream.failureReason && (
          <Tooltip title={stream.failureReason} placement="top">
            <Box
              component="span"
              sx={{ display: 'inline-flex', color: cx.red.main, cursor: 'help', flex: 'none' }}
              aria-label="Stream failure"
            >
              <TriangleAlert size={14} />
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Camera */}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <Box
            sx={{
              color: cx.text.primary,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={cameraName}
          >
            {cameraName}
          </Box>
          {stream.wsc && <WscBadge />}
        </Box>
        <Box
          sx={{
            color: cx.text.tertiary,
            fontSize: 11,
            lineHeight: '14px',
            mt: '2px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          tracking entity #{stream.trackedEntityId} ({stream.trackedEntityType})
        </Box>
      </Box>

      {/* Scheduled */}
      <Box sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {scheduledAt ? (
          <>
            <Box>{scheduledAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</Box>
            <Box sx={{ color: cx.text.muted, fontSize: 11, lineHeight: '14px', mt: '2px' }}>
              {scheduledAt.toLocaleDateString([], { month: 'short', day: 'numeric' })}
            </Box>
          </>
        ) : (
          <Box sx={{ color: cx.text.muted }}>—</Box>
        )}
      </Box>

      {/* Instance */}
      <Box sx={{ minWidth: 0, fontFamily: cx.fontMono, fontSize: 11, lineHeight: '14px' }}>
        <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {stream.instanceId ?? (
            <Box component="span" sx={{ color: cx.text.muted }}>
              —
            </Box>
          )}
        </Box>
        {stream.region && <Box sx={{ color: cx.text.muted, mt: '2px' }}>{stream.region}</Box>}
      </Box>

      {/* Endpoint, with the WSC alias (CNAME) under it when one was set */}
      <Box sx={{ minWidth: 0 }}>
        {!endpoint ? (
          <Box sx={{ color: cx.text.muted }}>—</Box>
        ) : (
          <EndpointValue url={endpoint} copyable={isCopyable} resolves={isRunning} />
        )}
        {cnameEndpoint && (
          <Box sx={{ mt: '4px' }}>
            <EndpointValue url={cnameEndpoint} copyable={isCopyable} resolves={isRunning} />
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '4px' }}>
        {!readOnly && isLaunchable && (
          <Tooltip title="Launch now, without waiting for the scheduler" placement="left">
            <span>
              <IconButton
                size="small"
                onClick={handleLaunch}
                disabled={launchStream.isPending}
                aria-label="Start stream now"
                sx={actionButtonSx}
              >
                <Play size={14} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {!readOnly && (
          <Tooltip
            title={
              isTerminal
                ? 'Stream is already terminated'
                : isInFlight
                  ? 'Cut the stream and tear down the instance'
                  : 'Remove this stream from the schedule'
            }
            placement="left"
          >
            <span>
              <IconButton
                size="small"
                onClick={handleDelete}
                disabled={isTerminal || deleteStream.isPending}
                aria-label="Delete stream"
                sx={{
                  ...actionButtonSx,
                  color: cx.text.muted,
                  '&:hover': { color: cx.red.main, bgcolor: cx.bg.elevated },
                }}
              >
                <Trash2 size={14} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
